import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import ClockIcon from "../../assets/clock.png";
import helloCodePlaceholder from "../../assets/hc-placeholder.svg";
import RestartIcon from "../../assets/refresh-arrow.png";
import StarsIcon from "../../assets/sparkling.png";
import { USER_TYPE } from "../../constants/common";
import { useTimer } from "../../hooks/useTimer";
import lessonServices from "../../services/lessonServices";
import { getLanguage } from "../../utils/getLanguage";
import { useNavigateWithLang } from "../../utils/navigation";
import LoadingSpinner from "../LoadingSpinner";
import MemoryGameFinishModal from "../modals/common/MemoryGameFinishModal";
import MemoryGameStartModal from "../modals/common/MemoryGameStartModal";

const MAX_TIME = 120;

const MemoryGame = ({ gameDetails }) => {
  const { id: lessonID } = useParams();
  const { t } = useTranslation();
  const lang = getLanguage();
  const navigate = useNavigateWithLang();
  const movesRef = useRef(0);

  const memoryGameStartModalRef = useRef();
  const memoryGameFinishModalRef = useRef();

  const { timeLeft, elapsedTime, start, stop } = useTimer(MAX_TIME, () => finishGame(true));

  const [isLoading, setIsLoading] = useState(false);
  const [cards, setCards] = useState([]);
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [moves, setMoves] = useState(0);
  const [gameFinished, setGameFinished] = useState(false);
  const [userGameId, setUserGameId] = useState(null);
  const [showAll, setShowAll] = useState(true);

  const initializeGame = async () => {
    setIsLoading(true);
    stop();
    resetState();

    try {
      const data = await lessonServices.getLessonGameStage(gameDetails?.id);
      setUserGameId(data?.user_game_id);

      const cards = data.cards.flatMap(({ image, index1, index2 }) => [
        { image, index: index1 },
        { image, index: index2 },
      ]);
      setCards(shuffle(cards));

      setTimeout(() => {
        setShowAll(false);
        start();
      }, 5000);
    } catch (err) {
      console.error("Failed to load game data:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    movesRef.current = moves;
  }, [moves]);

  const resetState = () => {
    setMatched([]);
    setFlipped([]);
    setMoves(0);
    setGameFinished(false);
    setShowAll(true);
  };

  const shuffle = (array) => [...array].sort(() => Math.random() - 0.5);

  const finishGame = (didTimeRunOut = false) => {
    stop();
    setGameFinished(true);
    memoryGameFinishModalRef.current?.openModal({
      timeSpent: elapsedTime,
      moves: movesRef.current,
      didTimeRunOut,
    });
  };

  const handleCardClick = (i) => {
    if (showAll || flipped.includes(i) || matched.includes(i) || flipped.length === 2) return;

    const newFlipped = [...flipped, i];
    setFlipped(newFlipped);

    if (newFlipped.length === 2) {
      setMoves((m) => m + 1);
      const [a, b] = newFlipped;
      if (cards[a].image === cards[b].image) {
        setMatched((prev) => [...prev, a, b]);
      }
      setTimeout(() => setFlipped([]), 1000);
    }
  };

  const handleGameCompletion = async () => {
    try {
      await lessonServices.submitGame(userGameId, {
        number_of_moves: moves,
        is_completed: true,
      });
      await lessonServices.finishLesson(lessonID);
      navigate(`/${USER_TYPE.STUDENT.ROLE}/learning-area`);
    } catch (err) {
      console.error("Game submit failed", err);
    }
  };

  useEffect(() => {
    // Delay opening to avoid calling setState during render
    const timer = setTimeout(() => {
      memoryGameStartModalRef.current?.openModal();
    }, 0);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!gameFinished && matched.length && matched.length === cards.length) {
      finishGame(false);
    }
  }, [matched, cards]);

  return (
    <div className="p-4">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between rounded-lg bg-blue-500 p-4 shadow-lg ring-4 ring-blue-300">
        <div className="flex items-center gap-2">
          <img className="w-12" src={ClockIcon} alt="Clock" />
          <span className={`min-w-10 text-lg ${timeLeft < 10 ? "text-red-500" : "text-white"}`}>
            {showAll ? MAX_TIME : timeLeft}
          </span>
        </div>
        <h2 className="flex items-center gap-2 text-3xl font-semibold text-white">
          <img className="w-9" src={StarsIcon} alt="star" />
          {t("memory_game_title")}
          <img className="w-9" src={StarsIcon} alt="star" />
        </h2>
        {showAll ? (
          <div className="min-w-24"></div>
        ) : (
          <div className="flex items-center gap-2">
            <span className="min-w-14"></span>
            <button onClick={initializeGame} className="transition hover:scale-110">
              <img className="w-10" src={RestartIcon} alt="Restart" />
            </button>
          </div>
        )}
      </div>

      {/* Game Grid */}
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <div
          className="grid gap-4"
          style={{ gridTemplateColumns: `repeat(${Math.min(cards.length, 6)}, 1fr)` }}
        >
          {cards.map((card, i) => {
            const isFlipped = showAll || flipped.includes(i) || matched.includes(i);
            return (
              <div key={i} onClick={() => handleCardClick(i)} className="perspective h-40 w-40">
                <div
                  className={`card-inner transform-style-preserve-3d relative h-full w-full transition-transform duration-500 ${
                    isFlipped ? "rotate-y-180" : ""
                  }`}
                >
                  {/* Back of the card (shown by default) */}
                  <div className="card-back backface-hidden absolute inset-0 flex h-full w-full items-center justify-center rounded-3xl border bg-blue-700">
                    <img src={helloCodePlaceholder} alt="Card Back" className="w-20" />
                  </div>

                  {/* Front of the card (revealed when flipped) */}
                  <div className="card-front backface-hidden rotate-y-180 absolute inset-0 h-full w-full transform overflow-hidden rounded-3xl border bg-white">
                    <img src={card.image} alt="Card Front" className="h-full w-full object-cover" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modals */}
      <MemoryGameStartModal ref={memoryGameStartModalRef} onSubmit={initializeGame} />
      <MemoryGameFinishModal
        ref={memoryGameFinishModalRef}
        onSubmit={handleGameCompletion}
        onSubmitReset={initializeGame}
      />
    </div>
  );
};

export default MemoryGame;
